/**
 * Spreadsheet upload helper. Import file pickers accept both CSV and XLSX (a
 * Google Sheets / Excel download). Plain CSV is returned verbatim; XLSX is
 * parsed to rows and serialised to CSV so it flows through the exact same CSV
 * parsers and /import preview endpoints — nothing downstream changes.
 *
 * The XLSX parser is dynamically imported, so it only loads when someone
 * actually picks an .xlsx (keeps it out of the main bundle).
 */

/** `accept` attribute for CSV + XLSX file inputs. */
export const SPREADSHEET_ACCEPT =
  ".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function isXlsx(file: File): boolean {
  return (
    /\.xlsx$/i.test(file.name) ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

/** CSV-escape one cell (dates → ISO date, everything else stringified). */
function cell(value: unknown): string {
  if (value == null) return "";
  const s = value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rowsToCsv(rows: unknown[][]): string {
  return rows.map((row) => (Array.isArray(row) ? row : []).map(cell).join(",")).join("\n");
}

/**
 * Normalise read-excel-file's return into flat rows. Depending on version/build
 * it returns EITHER `Cell[][]` (rows directly) OR `[{ sheet, data: Cell[][] }]`
 * (an array of sheet objects). We take the first sheet's rows in both cases.
 */
function firstSheetRows(parsed: unknown): unknown[][] {
  if (!Array.isArray(parsed) || parsed.length === 0) return [];
  const first = parsed[0] as unknown;
  if (first && typeof first === "object" && !Array.isArray(first) && Array.isArray((first as { data?: unknown }).data)) {
    return (first as { data: unknown[][] }).data;
  }
  return parsed as unknown[][];
}

/**
 * Read an uploaded file as CSV text. `.csv` → the file's text; `.xlsx` → the
 * first sheet parsed and serialised to CSV.
 */
export async function fileToCsv(file: File): Promise<string> {
  if (!isXlsx(file)) return file.text();
  // Browser build (Vite bundles it); dynamically imported so it only loads when
  // an .xlsx is actually chosen.
  const { default: readXlsxFile } = await import("read-excel-file/browser");
  const rows = firstSheetRows(await readXlsxFile(file));
  if (rows.length === 0) throw new Error("The spreadsheet's first sheet is empty.");
  return rowsToCsv(rows);
}
